<template lang="pug">
.insights-tile
  .tile-title {{ title }}
  .tile-range {{ rangeLabel }}
  .tile-value {{ value }}
  .tile-meta(v-if="meta") {{ meta }}
  v-chart.tile-chart(
    :option="chartOption"
    autoresize
    role="img"
    :aria-label="ariaLabel"
  )
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';
import VChart from 'vue-echarts';
import { CHART_COLOR, CHART_FILL, type ValueFormatter } from './echarts';

export interface TrendPoint {
  date: string;
  value: number;
}

export default defineComponent({
  name: 'InsightsTrendTile',
  components: { VChart },
  props: {
    title: { type: String, required: true },
    rangeLabel: { type: String, default: '' },
    value: { type: String, required: true },
    meta: { type: String, default: '' },
    points: { type: Array as PropType<TrendPoint[]>, default: () => [] },
    formatValue: {
      type: Function as PropType<ValueFormatter>,
      default: (value: number) => String(value),
    },
  },
  setup(props) {
    const chartOption = computed(() => ({
      animation: false,
      grid: {
        left: 0, right: 0, top: 8, bottom: 0,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { color: CHART_COLOR, width: 1 } },
        formatter: (params: Array<{ dataIndex: number }>) => {
          const point = props.points[params[0]?.dataIndex];
          if (!point) return '';
          return `${dayjs(point.date).format('MMM D, YYYY')}<br/>`
            + `<b>${props.formatValue(point.value)}</b>`;
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        show: false,
        data: props.points.map((point) => point.date),
      },
      yAxis: {
        type: 'value',
        show: false,
        min: 0,
        // Keep a flat zero series on the baseline instead of centered.
        max: (extent: { max: number }) => (extent.max > 0 ? extent.max * 1.1 : 1),
      },
      series: [{
        type: 'line',
        data: props.points.map((point) => point.value),
        smooth: false,
        symbol: 'none',
        lineStyle: { color: CHART_COLOR, width: 2 },
        areaStyle: { color: CHART_FILL },
      }],
    }));

    const ariaLabel = computed(() => `${props.title}: ${props.value}, ${props.rangeLabel}`);

    return { chartOption, ariaLabel };
  },
});
</script>

<style lang="scss" scoped>
.insights-tile {
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.25rem 0;
  min-width: 0;
}

.tile-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
}

.tile-range {
  font-size: 0.75rem;
  color: #6b7280;
}

.tile-value {
  margin-top: 0.75rem;
  font-size: 1.875rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
}

.tile-meta {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.tile-chart {
  height: 90px;
  margin-top: 0.75rem;
}
</style>
